import { Close } from "@faire/slate-icons/Close";
import { Information } from "@faire/slate-icons/Information";
import { AccordionGroup } from "@faire/slate/components/accordion";
import { IconButton } from "@faire/slate/components/icon-button";
import { TextInput } from "@faire/slate/components/text-input";
import { Tooltip } from "@faire/slate/components/tooltip";
import { useEffect, useMemo, useState } from "react";

import { RoasComparisonBar } from "./RoasComparisonBar";
import { computeBreakevenRoas, formatRoas } from "./roasScale";

const MIN_PROFIT_MARGIN = 1;
const MAX_PROFIT_MARGIN = 80;
const DEFAULT_PROFIT_MARGIN = 5;

const DRAWER_TOOLTIP_Z_INDEX = 2100;

const PROFIT_MARGIN_TOOLTIP =
  "Profit margin is the share of each sale you keep after what the product costs you to make or buy, and after Faire fees. It's a single blended number across your catalog.";

const ESTIMATE_PROFIT_MARGIN_TOOLTIP = `Enter the share of each sale you keep, measured the same way each time:
( sale − product cost − Faire fees )
÷ sale
Don't include ad spend — that's what we're testing. Leave out shipping to the retailer and overhead.`;

const formatCurrency = (value: number): string =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const BreakevenRoasDrawer: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  const [profitMargin, setProfitMargin] = useState(DEFAULT_PROFIT_MARGIN);

  const breakevenRoas = useMemo(
    () => computeBreakevenRoas(profitMargin),
    [profitMargin]
  );

  const salesNeededPerDollar = breakevenRoas;

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <style>{`
        @keyframes breakevenDrawerFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes breakevenDrawerSlideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .profit-margin-slider {
          -webkit-appearance: none;
          appearance: none;
          height: 4px;
          border-radius: 9999px;
          background: #dfe0e1;
        }
        .profit-margin-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 9999px;
          background: #333333;
          cursor: pointer;
        }
        .profit-margin-slider::-moz-range-thumb {
          width: 16px;
          height: 16px;
          border: none;
          border-radius: 9999px;
          background: #333333;
          cursor: pointer;
        }
      `}</style>
      <button
        type="button"
        className="fixed inset-0 z-[1999] animate-[breakevenDrawerFadeIn_0.2s_ease-out] bg-(--slate-color-grey-1000)/50"
        aria-label="Close profit calculator"
        onClick={onClose}
      />
      <div
        className="bg-fs-surface-primary fixed top-0 right-0 z-[2000] flex h-screen w-[min(100vw,720px)] animate-[breakevenDrawerSlideIn_0.3s_ease-out] flex-col shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="breakeven-roas-drawer-title"
        data-test-id="breakeven-roas-drawer"
      >
        <header className="border-fs-border-muted shrink-0 border-b px-10 pt-6 pb-5">
          <div className="mb-5 flex items-center justify-between gap-4">
            <span className="type-fs-label-medium text-fs-text-subdued tracking-[0.08em] uppercase">
              Profit calculator
            </span>
            <IconButton
              variant="tertiary"
              size="xSmall"
              Icon={Close}
              aria-label="Close"
              onClick={onClose}
            />
          </div>
          <span className="type-fs-label-medium text-fs-text-subdued mb-3 block tracking-[0.08em] uppercase">
            Promoted listings
          </span>
          <h1
            id="breakeven-roas-drawer-title"
            className="type-fs-display-m text-fs-text-primary"
          >
            Will advertising pay off for you?
          </h1>
        </header>

        <div className="flex flex-1 flex-col overflow-y-auto px-10 pt-6 pb-10">
          <p className="type-fs-paragraph text-fs-text-primary mb-8">
            Your{" "}
            <span className="type-fs-paragraph-medium">breakeven ROAS</span> is
            the return your ads need just to cover their cost. Enter your profit
            margin to see it.
          </p>

          <div className="mb-8 flex flex-col gap-3">
            <div className="flex items-center justify-between gap-4">
              <Tooltip
                role="description"
                label={PROFIT_MARGIN_TOOLTIP}
                triggerAriaLabel="Profit margin definition"
                zIndex={DRAWER_TOOLTIP_Z_INDEX}
                position="bottom"
              >
                <span className="type-fs-paragraph-medium text-fs-text-primary border-fs-border-emphasis cursor-default border-b border-dotted">
                  Your profit margin
                </span>
              </Tooltip>
              <Tooltip
                role="additional-info"
                label={ESTIMATE_PROFIT_MARGIN_TOOLTIP}
                triggerAriaLabel="How to estimate profit margin"
                zIndex={DRAWER_TOOLTIP_Z_INDEX}
                position="bottom"
                alignment="end"
              >
                <button
                  type="button"
                  className="type-fs-paragraph text-fs-action-text-default cursor-default"
                >
                  How do I estimate this?
                </button>
              </Tooltip>
            </div>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min={MIN_PROFIT_MARGIN}
                max={MAX_PROFIT_MARGIN}
                value={profitMargin}
                onChange={(event) =>
                  setProfitMargin(Number(event.target.value))
                }
                className="profit-margin-slider min-w-0 flex-1 cursor-pointer"
                aria-label="Profit margin"
              />
              <div className="w-[72px] shrink-0">
                <TextInput
                  value={`${profitMargin} %`}
                  onChange={(event) => {
                    const parsed = Number.parseInt(
                      event.target.value.replace(/[^0-9]/g, ""),
                      10
                    );
                    if (Number.isNaN(parsed)) {
                      return;
                    }
                    setProfitMargin(
                      Math.min(
                        MAX_PROFIT_MARGIN,
                        Math.max(MIN_PROFIT_MARGIN, parsed)
                      )
                    );
                  }}
                  aria-label="Profit margin percentage"
                />
              </div>
            </div>
          </div>

          <div className="border-fs-border-muted mb-8 rounded-lg border px-6 py-6">
            <div className="mb-6 flex flex-col gap-6 min-[560px]:flex-row min-[560px]:items-start min-[560px]:justify-between">
              <div>
                <p className="type-fs-paragraph text-fs-text-subdued mb-1">
                  Your breakeven ROAS
                </p>
                <p className="type-fs-display-s text-fs-text-primary">
                  {formatRoas(breakevenRoas)}
                </p>
              </div>
              <p className="type-fs-paragraph text-fs-text-primary max-w-[300px]">
                For every <strong>$1</strong> you spend on ads, you need about{" "}
                <strong>{formatCurrency(salesNeededPerDollar)}</strong> in sales
                just to break even.
              </p>
            </div>
            <RoasComparisonBar breakevenRoas={breakevenRoas} yourRoas={null} />
          </div>

          <AccordionGroup
            items={[
              {
                label: "How we calculate this",
                content: (
                  <div className="flex flex-col gap-4">
                    <div className="bg-fs-surface-secondary type-fs-paragraph text-fs-text-primary rounded-lg px-4 py-3 font-[ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,monospace]">
                      breakeven ROAS = 1 ÷ profit margin
                    </div>
                    <div className="type-fs-paragraph text-fs-text-primary flex flex-col gap-2">
                      <div className="flex items-center justify-between gap-4 border-b border-dotted pb-2">
                        <span>Profit margin</span>
                        <span>{profitMargin}%</span>
                      </div>
                      <div className="flex items-center justify-between gap-4">
                        <span>{`1 ÷ ${(profitMargin / 100).toFixed(2)}`}</span>
                        <span className="type-fs-paragraph-medium">
                          {formatRoas(breakevenRoas)}
                        </span>
                      </div>
                    </div>
                    <p className="type-fs-paragraph text-fs-text-subdued">
                      This estimate assumes your margin already reflects product
                      costs and Faire fees. Ad spend is evaluated against
                      attributed sales from Promoted Listings.
                    </p>
                  </div>
                ),
              },
              {
                label: "Key terms",
                content: (
                  <div className="flex flex-col gap-5">
                    <div className="flex flex-col gap-1">
                      <p className="type-fs-paragraph-medium text-fs-text-primary">
                        ROAS
                      </p>
                      <p className="type-fs-paragraph text-fs-text-subdued">
                        Return on ad spend — sales attributed to your ads
                        divided by the cost of them. A 4x ROAS means $4 in sales
                        per $1 spent.
                      </p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <p className="type-fs-paragraph-medium text-fs-text-primary">
                        Profit margin
                      </p>
                      <p className="type-fs-paragraph text-fs-text-subdued">
                        The share of each sale you keep after product costs and
                        Faire fees.
                      </p>
                    </div>
                    <div className="flex flex-col gap-1">
                      <p className="type-fs-paragraph-medium text-fs-text-primary">
                        Breakeven ROAS
                      </p>
                      <p className="type-fs-paragraph text-fs-text-subdued">
                        The ROAS where ads exactly cover their cost. Below this,
                        ads cost more than they return.
                      </p>
                    </div>
                    <div className="border-fs-border-muted flex items-start gap-2 border-t pt-6">
                      <span className="text-fs-text-subdued mt-0.5 shrink-0">
                        <Information width="16px" height="16px" />
                      </span>
                      <p className="type-fs-paragraph text-fs-text-subdued">
                        This is a directional estimate to help you plan. It does
                        not include shipping or ops costs, and attribution
                        uncertainty.
                      </p>
                    </div>
                  </div>
                ),
              },
            ]}
          />
        </div>
      </div>
    </>
  );
};
